import { ChangeDetectionStrategy, Component, inject, Injectable, NgModule, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
    MatDialogConfig,
    MatDialogRef,
    MAT_DIALOG_DATA,
    MatDialogModule,
    MatDialog,
    MAT_DIALOG_DEFAULT_OPTIONS,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
    CoreDialogModule,
    PCPS_DIALOGS_DEFAULT_OPTIONS,
    PCPSDialogsDefaultOptions,
    ScreenSizeService,
} from '@polk_county_public_schools/ngx-pcps-dialogs/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { passwordComplexityValidator } from '../../../../validators/pwd-complexity.ng';

// #region Custom Interfaces

export interface CreateAccountDialogOptions extends MatDialogConfig {
    CancelText?: string;
    ClassicCancel?: boolean;
    MessageText?: string;
    OKText?: string;
    TitleText?: string;
}

interface AccountFormGroup {
    firstName: FormControl<string>;
    lastName: FormControl<string>;
    email: FormControl<string>;
    password: FormControl<string>;
    password2: FormControl<string>;
}

export interface CreateAccountDialogResult {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
}

// #endregion

@Component({
    selector: 'create-account-dialog',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [MatDialogModule, MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, ReactiveFormsModule],
    host: {
        class: 'block relative',
    },
    templateUrl: 'create-account-dlg.ng.html',
})
export class CreateAccountDialogComponent {
    // #region Injects

    protected readonly dialogRef = inject(MatDialogRef<CreateAccountDialogComponent>);
    protected readonly data = inject<CreateAccountDialogOptions>(MAT_DIALOG_DATA);
    protected readonly fb = inject(FormBuilder);
    protected readonly pcpsDefaults = inject<PCPSDialogsDefaultOptions>(PCPS_DIALOGS_DEFAULT_OPTIONS, { optional: true });

    // #endregion

    // #region Properties

    protected readonly canceltext = this.data?.CancelText ?? 'Cancel';
    protected readonly classicCancel = this.data?.ClassicCancel ?? this.pcpsDefaults?.useClassicCancel ?? false;
    protected readonly message = this.data?.MessageText ?? '';
    protected readonly oktext = this.data?.OKText ?? 'OK';
    protected readonly title = this.data?.TitleText ?? 'New Account Details...';

    protected readonly accountForm = this.fb.group<AccountFormGroup>(
        {
            firstName: this.fb.control<string>('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(50)] }),
            lastName: this.fb.control<string>('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(50)] }),
            email: this.fb.control<string>('', {
                nonNullable: true,
                validators: [Validators.required, Validators.email, Validators.maxLength(150)],
            }),
            password: this.fb.control<string>('', {
                nonNullable: true,
                validators: [Validators.required, passwordComplexityValidator()],
            }),
            password2: this.fb.control<string>('', { nonNullable: true, validators: [Validators.required] }),
        },
        {
            validators: (fgrp: AbstractControl) => {
                const formval = (fgrp as FormGroup).value;

                // Ensure that the two password values match up.
                return formval.password !== formval.password2
                    ? {
                          verifyMismatch: true,
                      }
                    : null;
            },
        }
    );

    // #endregion

    // #region OK Handler

    OKHandler() {
        if (!this.accountForm.valid) {
            // Mark all controls as touched to get errors to display.
            this.accountForm.markAllAsTouched();
            return;
        }

        const actInfo = this.accountForm.value;

        this.dialogRef.close(<CreateAccountDialogResult>{
            firstName: actInfo.firstName,
            lastName: actInfo.lastName,
            email: actInfo.email,
            password: actInfo.password,
        });
    }

    // #endregion
}

@Injectable()
export class CreateAccountDialogService {
    // #region Injects

    protected readonly matDialog = inject(MatDialog);
    protected readonly mediaChange = inject(ScreenSizeService);
    protected readonly defaultOpts = inject<MatDialogConfig>(MAT_DIALOG_DEFAULT_OPTIONS, { optional: true });

    // #endregion

    // #region Properties

    protected readonly smallMediaSize = this.mediaChange?.ScreenSize?.IsSmallDisplay || false;

    // #endregion

    // #region Close All Dialogs Service Call

    CloseAllDialogs(): void {
        this.matDialog.closeAll();
    }

    // #endregion

    // #region Open

    Open(options?: CreateAccountDialogOptions): Observable<CreateAccountDialogResult | null> {
        const dlgoptions: CreateAccountDialogOptions = {
            data: {
                TitleText: 'Create New Account...',
                MessageText: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options,
            },
            // Passthrus
            id: options?.id ?? this.defaultOpts?.id,
            role: options?.role ?? this.defaultOpts?.role ?? 'dialog',
            panelClass: !!options?.panelClass
                ? Array.isArray(options.panelClass)
                    ? options.panelClass
                    : [options.panelClass]
                : (this.defaultOpts?.panelClass ?? ''),
            hasBackdrop: options?.hasBackdrop ?? this.defaultOpts?.hasBackdrop ?? true,
            backdropClass: options?.backdropClass ?? this.defaultOpts?.backdropClass ?? '',
            disableClose: options?.disableClose != null ? options.disableClose : (this.defaultOpts?.disableClose ?? false),
            width: options?.width ?? this.defaultOpts?.width ?? '',
            height: options?.height ?? this.defaultOpts?.height ?? '',
            minWidth: options?.minWidth ?? this.defaultOpts?.minWidth,
            minHeight: options?.minHeight ?? this.defaultOpts?.minHeight,
            maxWidth: options?.maxWidth ?? this.defaultOpts?.maxWidth ?? '80vw',
            maxHeight: options?.maxHeight ?? this.defaultOpts?.maxHeight,
            position: options?.position ?? this.defaultOpts?.position,
            direction: options?.direction ?? this.defaultOpts?.direction,
            ariaDescribedBy: options?.ariaDescribedBy ?? this.defaultOpts?.ariaDescribedBy ?? null,
            ariaLabelledBy: options?.ariaLabelledBy ?? this.defaultOpts?.ariaLabelledBy ?? null,
            ariaLabel: options?.ariaLabel ?? this.defaultOpts?.ariaLabel ?? null,
            ariaModal: options?.ariaModal ?? this.defaultOpts?.ariaModal ?? true,
            autoFocus: options?.autoFocus ?? this.defaultOpts?.autoFocus ?? 'first-tabbable',
            restoreFocus: options?.restoreFocus ?? this.defaultOpts?.restoreFocus ?? true,
            delayFocusTrap: options?.delayFocusTrap ?? this.defaultOpts?.delayFocusTrap ?? true,
            scrollStrategy: options?.scrollStrategy ?? this.defaultOpts?.scrollStrategy,
            closeOnNavigation: options?.closeOnNavigation ?? this.defaultOpts?.closeOnNavigation ?? true,
            enterAnimationDuration: options?.enterAnimationDuration ?? this.defaultOpts?.enterAnimationDuration,
            exitAnimationDuration: options?.exitAnimationDuration ?? this.defaultOpts?.exitAnimationDuration,
        };

        // if (this.smallMediaSize) {
        //     dlgoptions.width = '80vw';
        //     dlgoptions.panelClass = [...(dlgoptions.panelClass as string[])];
        // } else {
        //     dlgoptions.panelClass = [...(dlgoptions.panelClass as string[])];
        // }

        const openHandle = this.matDialog.open<CreateAccountDialogComponent>(CreateAccountDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion
}

@NgModule({
    imports: [CreateAccountDialogComponent, CoreDialogModule],
    declarations: [],
    exports: [CreateAccountDialogComponent],
    providers: [CreateAccountDialogService],
})
export class CreateAccountDialogModule {}
